import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { type CodeableConcept, Immunization } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface AddImmunizationRequest {
  patientId: string;
  dateAdministered: string;
  vaccineCode: Pick<CodeableConcept, "coding" | "text">;
}

export const useAddImmunization = ({
  patientId,
  onSuccess,
  onError,
}: {
  patientId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation({
    mutationOptions: {
      mutationFn: (data: AddImmunizationRequest) =>
        axiosInstance.post("/charting/immunization/", data),
      mutationKey:
        QueryKeys.Patient.MedicalRecords.Immunization.CREATE(patientId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Immunization.DEFAULT(patientId),
        });
        onSuccess?.();
      },
      onError,
    },
  });
};

interface GetImmunizationResponse {
  immunizations: Immunization[];
}

export const useGetImmunization = ({ patientId }: { patientId: string }) => {
  return useApi<GetImmunizationResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Patient.MedicalRecords.Immunization.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/charting/immunization/", {
          params: { patientId },
        });
        const data: GetImmunizationResponse = response?.data;
        data.immunizations = data.immunizations.map(
          (resource: object) => new Immunization(resource),
        );
        return data;
      },
      enabled: Boolean(patientId),
    },
  });
};

export const useVoidImmunization = ({
  immunizationId,
  patientId,
  onSuccess,
}: {
  immunizationId: string;
  patientId: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<void, void>({
    mutationOptions: {
      mutationFn: () =>
        axiosInstance.delete(`/charting/immunization/${immunizationId}`),
      mutationKey: QueryKeys.Patient.MedicalRecords.Immunization.VOID(
        patientId,
        immunizationId,
      ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.Immunization.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

interface VaccineCode {
  code: string;
  display: string;
  system: string;
}

interface VaccineCodeSearchResponse {
  results: VaccineCode[];
}

export const useVaccineCodeSearch = (searchTerm: string) => {
  return useApi<VaccineCodeSearchResponse>({
    queryOptions: {
      queryKey:
        QueryKeys.Reference.Immunization.VACCINE_CODE_SEARCH(searchTerm),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/immunization/search/vaccine-codes",
          {
            params: { searchTerm },
          },
        );
        return response?.data;
      },
      enabled: searchTerm.length > 2,
    },
  });
};
